import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { blogAPI } from '../services/api';
import Header from './Header';
import Footer from './Footer';
import SEOHead from './SEOHead';
import { Calendar, BookOpen, ArrowRight, Sparkles } from 'lucide-react';

const BlogPage = () => {
  const { currentLanguage, t } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Inject `<link rel="alternate" type="application/rss+xml">` so feed
  // readers (Feedly, NetNewsWire, browser auto-discovery) can find the
  // per-language RSS feed for this blog.
  useEffect(() => {
    const id = 'blog-rss-alternate';
    document.querySelectorAll(`link#${id}`).forEach((n) => n.remove());
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'alternate';
    link.type = 'application/rss+xml';
    link.title = `PenLab Peptide Research Blog (${currentLanguage})`;
    const apiBase = process.env.REACT_APP_BACKEND_URL || '';
    link.href = `${apiBase}/api/feeds/blog-${currentLanguage}.xml`;
    document.head.appendChild(link);
    return () => link.remove();
  }, [currentLanguage]);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const blogPosts = await blogAPI.getPosts();
        setPosts(blogPosts);
      } catch (err) {
        console.error('Error fetching blog posts:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const getBlogTitle = (post) => {
    return post.title[currentLanguage] || post.title.en || post.title;
  };

  const getBlogExcerpt = (post) => {
    return post.excerpt?.[currentLanguage] || post.excerpt?.en || '';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    // Map our 9 supported langs to BCP-47 locales for Intl.DateTimeFormat.
    const localeMap = {
      en: 'en-US', es: 'es-ES', ru: 'ru-RU', de: 'de-DE', fr: 'fr-FR',
      it: 'it-IT', pl: 'pl-PL', nl: 'nl-NL', pt: 'pt-BR',
    };
    return date.toLocaleDateString(localeMap[currentLanguage] || 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const pageContent = {
    en: { title: 'Research Blog', subtitle: 'Latest news, research insights and scientific updates from PenLab Peptide', readMore: 'Read Article' },
    es: { title: 'Blog de Investigación', subtitle: 'Últimas noticias, información de investigación y actualizaciones científicas de PenLab Peptide', readMore: 'Leer Artículo' },
    ru: { title: 'Блог исследований', subtitle: 'Новости, научные материалы и обновления от PenLab Peptide', readMore: 'Читать статью' },
    de: { title: 'Forschungsblog', subtitle: 'Neueste Nachrichten, Forschungseinblicke und wissenschaftliche Updates von PenLab Peptide', readMore: 'Artikel lesen' },
    fr: { title: 'Blog de Recherche', subtitle: 'Dernières actualités, aperçus de recherche et mises à jour scientifiques de PenLab Peptide', readMore: 'Lire l\'article' },
    it: { title: 'Blog di Ricerca', subtitle: 'Ultime notizie, approfondimenti di ricerca e aggiornamenti scientifici di PenLab Peptide', readMore: 'Leggi l\'articolo' },
    pl: { title: 'Blog badawczy', subtitle: 'Najnowsze wiadomości, wgląd badawczy i aktualizacje naukowe od PenLab Peptide', readMore: 'Przeczytaj artykuł' },
    nl: { title: 'Onderzoeksblog', subtitle: 'Laatste nieuws, onderzoeksinzichten en wetenschappelijke updates van PenLab Peptide', readMore: 'Artikel lezen' },
    pt: { title: 'Blog de Pesquisa', subtitle: 'Últimas notícias, insights de pesquisa e atualizações científicas da PenLab Peptide', readMore: 'Ler artigo' },
  };

  const c = pageContent[currentLanguage] || pageContent.en;

  return (
    <div className="min-h-screen bg-capsule-bg">
      <SEOHead page="blog" />
      <Header />

      {/* Hero Banner - Capsule 8 Design */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-capsule-teal-dark">
        {/* DNA Decoration */}
        <div className="absolute right-0 top-0 bottom-0 w-96 opacity-20">
          <svg viewBox="0 0 200 400" fill="none" className="w-full h-full">
            <defs>
              <linearGradient id="blogGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#14B8A6" />
                <stop offset="100%" stopColor="#22D3EE" />
              </linearGradient>
            </defs>
            <path d="M50 0 Q120 50 50 100 Q-20 150 50 200 Q120 250 50 300 Q-20 350 50 400" 
                  stroke="url(#blogGradient)" strokeWidth="3" fill="none"/>
            <path d="M150 0 Q80 50 150 100 Q220 150 150 200 Q80 250 150 300 Q220 350 150 400" 
                  stroke="url(#blogGradient)" strokeWidth="3" fill="none"/>
          </svg>
        </div>
        
        {/* Floating spheres */}
        <div className="absolute top-20 right-32 w-20 h-20 rounded-full bg-gradient-to-br from-capsule-rose-gold/30 to-capsule-salmon/20 blur-xl"></div>
        <div className="absolute bottom-20 right-48 w-16 h-16 rounded-full bg-gradient-to-br from-capsule-teal/30 to-capsule-aqua/20 blur-xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-6 border border-white/20">
              <BookOpen className="w-4 h-4 text-capsule-aqua" />
              <span className="text-sm font-medium text-capsule-aqua">{t('blog_scientific_insights')}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              {c.title}
            </h1>
            <p className="text-gray-300 text-lg max-w-2xl leading-relaxed">
              {c.subtitle}
            </p>
          </div>
        </div>
        
        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 60L48 55C96 50 192 40 288 35C384 30 480 30 576 33.3C672 37 768 43 864 45C960 47 1056 45 1152 41.7C1248 38 1344 33 1392 30.8L1440 29V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0Z" fill="#E7EAED"/>
          </svg>
        </div>
      </div>

      {/* Blog Posts */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="bg-white/50 rounded-3xl h-48 mb-4"></div>
                <div className="h-4 bg-white/50 rounded-xl mb-2"></div>
                <div className="h-4 bg-white/50 rounded-xl w-3/4"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <Link to={`/blog/${post.slug}`} key={post.id}>
                <div className="group glass-card rounded-3xl overflow-hidden hover:shadow-glass-hover transition-all duration-500 hover:-translate-y-2 h-full flex flex-col">
                  <div className="relative overflow-hidden">
                    <img
                      src={post.image_url || '/placeholder-blog.png'}
                      alt={getBlogTitle(post)}
                      className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-700"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/placeholder-blog.png';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center text-sm text-gray-500 mb-3">
                      <Calendar className="w-4 h-4 mr-2 text-capsule-teal" />
                      <span>{formatDate(post.published_date)}</span>
                    </div>
                    <h3 className="font-bold text-gray-900 mb-3 line-clamp-2 leading-tight group-hover:text-capsule-teal transition-colors">
                      {getBlogTitle(post)}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed line-clamp-3 mb-4 flex-1">
                      {getBlogExcerpt(post)}
                    </p>
                    <div className="flex items-center text-capsule-teal font-medium text-sm group-hover:text-capsule-teal-dark transition-colors">
                      <span>{c.readMore}</span>
                      <ArrowRight className="ml-2 w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* No posts message */}
        {!loading && posts.length === 0 && (
          <div className="text-center py-12">
            <div className="glass-card inline-block rounded-3xl p-8">
              <Sparkles className="w-12 h-12 text-capsule-teal mx-auto mb-4" />
              <p className="text-gray-600 text-lg">
                {t('blog_more_articles_coming')}
              </p>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default BlogPage;
